import type { LucideIcon } from "lucide-react";
import { AlertTriangle, CheckCircle2, Clock3, Info, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export type StatusTone = "neutral" | "info" | "success" | "warning" | "danger";

const statusStyles: Record<StatusTone, string> = {
  neutral: "border-slate-200 bg-slate-50 text-slate-700",
  info: "border-blue-200 bg-blue-50 text-blue-700",
  success: "border-emerald-200 bg-emerald-50 text-emerald-700",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
  danger: "border-red-200 bg-red-50 text-red-700",
};

const statusIcons: Record<StatusTone, LucideIcon> = {
  neutral: Clock3,
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
};

interface StatusBadgeProps {
  children: React.ReactNode;
  tone?: StatusTone;
  icon?: LucideIcon | null;
  className?: string;
}

export function StatusBadge({
  children,
  tone = "neutral",
  icon,
  className,
}: StatusBadgeProps) {
  const Icon = icon === null ? null : icon || statusIcons[tone];

  return (
    <Badge
      variant="outline"
      className={cn("gap-1 whitespace-nowrap font-medium", statusStyles[tone], className)}
    >
      {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
      {children}
    </Badge>
  );
}
